<template>
  <div class="row">
    <div class="col-lg-4 col-sm-12" v-for="card in cards" :key="card.title">
      <DashboardCard :card="card" />
    </div>
    <div class="col-12 row no-gutters mb-3">
      <div class="col-12 px-0 offset-md-8 col-md-4 select-filters">
        <Multiselect
          v-model="filter"
          :options="filterOptions"
          :multiple="false"
          group-values="options"
          group-label="type"
          :group-select="false"
          :placeholder="$t('services/concierge_dashboard.dropdown_filter.placeholder')"
          :select-label="translations.selectLabel"
          :deselect-label="translations.deselectLabel"
          :search-placeholder="translations.searchPlaceholder"
          track-by="value"
          label="label"
          @remove="resetFilter()"
          @select="filterSelection()"
        >
          <span slot="noResult">
            {{ translations.searchNoResults }}
          </span>
          <span slot="noOptions">
            {{ translations.noOptions }}
          </span>
        </Multiselect>
      </div>
    </div>

    <div class="col-12">
      <NavTabs :active-tab-index="step" :tabs="tabs" pills @selectTab="changeTab">
        <NavTabContent
          :title="getTab('step1').title"
          :active="getTab('step1').tabIndex == step"
          :disabled="getTab('step1').disabled"
        >
          <div class="my-4">
            <DashboardTodayAppointments :todays-appointments="todaysAppointments" />
            <DashboardWeeklyAppointments :weekly-appointments="weeklyAppointments" />
            <DashboardNextWeekAppointments :next-week-appointments="nextWeekAppointments" />
          </div>
        </NavTabContent>
        <NavTabContent
          :title="getTab('step2').title"
          :active="getTab('step2').tabIndex == step"
          :disabled="getTab('step2').disabled"
        >
          <DashboardCalendar
            :business-hours="businessHours"
            :week-length="weekLength"
            :all-events="allEvents"
            :test-drive-types="testDriveTypes"
            :meeting-types="meetingTypes"
          />
        </NavTabContent>
      </NavTabs>
    </div>
  </div>
</template>
<script>
import { defineComponent, ref } from 'vue'
import NavTabs from '@components/commons/navigation/NavTabs.vue'
import NavTabContent from '@components/commons/navigation/NavTabContent.vue'
import DashboardCalendar from '@components/concierge/DashboardCalendar.vue'
import DashboardWeeklyAppointments from '@components/concierge/DashboardWeeklyAppointments.vue'
import DashboardTodayAppointments from '@components/concierge/DashboardTodayAppointments.vue'
import DashboardNextWeekAppointments from '@components/concierge/DashboardNextWeekAppointments.vue'
import DashboardCard from '@components/concierge/DashboardCard.vue'
import Multiselect from 'vue-multiselect'
import useMultiselectTranslations from '@composables/useMultiselectTranslations'
import { router } from '@inertiajs/vue3'
import { getActiveLanguage, wTrans } from 'laravel-vue-i18n'

export default defineComponent({
  name: 'DashboardIndex',
  components: {
    NavTabs,
    NavTabContent,
    DashboardCalendar,
    DashboardWeeklyAppointments,
    DashboardTodayAppointments,
    DashboardNextWeekAppointments,
    DashboardCard,
    Multiselect
  },
  props: {
    allEvents: {
      type: Array,
      default() {
        return []
      }
    },
    testDriveTypes: {
      type: Array,
      default() {
        return []
      }
    },
    meetingTypes: {
      type: Array,
      default() {
        return []
      }
    },
    businessHours: {
      type: Array,
      default() {
        return []
      }
    },
    weekLength: {
      type: Number,
      default: 7
    },
    todaysAppointments: {
      type: Array,
      default() {
        return []
      }
    },
    weeklyAppointments: {
      type: Array,
      default() {
        return []
      }
    },
    nextWeekAppointments: {
      type: Array,
      default() {
        return []
      }
    },

    cards: {
      type: Array,
      default() {
        return []
      }
    },
    filterOptions: {
      type: Array,
      default() {
        return []
      }
    },
    selected: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      step: 0
    }
  },
  setup(props) {
    const filter = ref(null)
    const { allTranslations } = useMultiselectTranslations(getActiveLanguage())
    const translations = allTranslations

    if (props.selected) {
      for (const filterGroup of props.filterOptions) {
        const selection = filterGroup.options.find(option => option.value === props.selected)
        if (selection) {
          filter.value = selection
        }
      }
    }

    return {
      filter,
      translations
    }
  },
  computed: {
    tabs() {
      return [
        {
          title: wTrans('services/concierge_dashboard.tabs.dashboard').value,
          step: 'step1',
          disabled: false,
          tabIndex: 0,
          icon: 'fa-table'
        },
        {
          title: wTrans('services/concierge_dashboard.tabs.calendar').value,
          step: 'step2',
          disabled: false,
          tabIndex: 1,
          icon: 'fa-calendar'
        }
      ]
    }
  },
  methods: {
    resetFilter() {
      router.visit(route('concierge.index'), {
        only: ['allEvents', 'todaysAppointments', 'weeklyAppointments', 'nextWeekAppointments'],
        preserveState: true,
        preserveScroll: true
      })
    },
    filterSelection() {
      router.reload({
        data: {
          filter: {
            type: this.filter.value.split('_').shift(),
            id: this.filter.id
          }
        },
        only: ['allEvents', 'todaysAppointments', 'weeklyAppointments', 'nextWeekAppointments'],
        preserveState: true,
        preserveScroll: true
      })
    },
    changeTab(tabIndex) {
      if (!this.getTabByIndex(tabIndex)?.disabled) {
        this.step = tabIndex
      }
    },
    getTabByIndex(tabIndex) {
      return this.tabs.find(x => x.tabIndex == tabIndex)
    },
    getTab(step) {
      return this.tabs.find(x => x.step == step)
    }
  }
  // Add any component-specific options here if needed
})
</script>
<style>
@media screen and (min-width: 768px) {
  .select-filters {
    position: absolute;
    top: 16px;
    right: 7.5px;
    z-index: 999;
  }
}

.multiselect__option--group.multiselect__option--disabled {
  background-color: #6c757d !important;
}
</style>
